import { buildKitchenTicketHtml } from '@/services/printing/kitchenTicketPrinter';
import { renderOperationalPrintEvent } from '@/services/printing/operationalPrintTemplateService';
import {
  buildPrintJob,
  PRINT_EVENT_KEYS,
  toPrintAgentPayload,
} from '@/services/printing/printJobService';

function escapeText(value) {
  return String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
}

function formatDateTime(value) {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function formatMoney(value) {
  return `${normalizeNumber(value).toFixed(2)} €`;
}

function buildItemLabel(item = {}) {
  const quantity = normalizeNumber(item.amount ?? item.quantity ?? 0);
  const name = item.plate_name || item.product_name_snapshot || item.name || item.plate?.name || 'Producto';
  return `${quantity > 0 ? `${quantity}x ` : ''}${name}`;
}

function buildItemLines(item = {}) {
  const lines = [buildItemLabel(item)];

  const comment = escapeText(item.comment || item.notes || '');
  if (comment) {
    lines.push(`  Nota: ${comment}`);
  }

  const steps = Array.isArray(item.steps_selected)
    ? item.steps_selected
    : Array.isArray(item.steps)
      ? item.steps
      : [];

  steps.forEach((step, index) => {
    const stepName = step?.name || step?.title || `Paso ${index + 1}`;
    const selectedPlates = Array.isArray(step?.plates_selected)
      ? step.plates_selected
      : Array.isArray(step?.plates)
        ? step.plates
        : [];
    const selectedNames = selectedPlates
      .map((selected) => selected?.plate?.name || selected?.custom_name || selected?.name || selected?.plate_name || '')
      .filter(Boolean);

    if (selectedNames.length) {
      lines.push(`  ${stepName}: ${selectedNames.join(', ')}`);
    }
  });

  return lines;
}

function extractItems(source = {}) {
  if (Array.isArray(source.items) && source.items.length) {
    return source.items;
  }

  if (Array.isArray(source.shopping_carts) && source.shopping_carts.length) {
    return source.shopping_carts.map((cart) => ({
      amount: cart.amount ?? cart.quantity ?? 0,
      plate_name: cart.product_name_snapshot || cart.plate?.name || cart.name || 'Producto',
      comment: cart.comment || '',
      steps_selected: cart.steps_selected || [],
      unit_price: cart.unit_price ?? cart.plate?.price ?? 0,
      category_id: cart.category_id || cart.product_category_id || cart.category?.id || cart.plate?.category_id || cart.plate?.category?.id || null,
    }));
  }

  return [];
}

function normalizeFingerprintPayload(payload = {}) {
  const items = extractItems(payload).map((item) => ({
    name: item.plate_name || item.product_name_snapshot || item.name || 'Producto',
    amount: normalizeNumber(item.amount ?? item.quantity ?? 0),
    unit_price: normalizeNumber(item.unit_price ?? item.price ?? 0),
    comment: escapeText(item.comment || item.notes || ''),
    steps: Array.isArray(item.steps_selected)
      ? item.steps_selected.map((step) => ({
          name: step?.name || '',
          plates: Array.isArray(step?.plates)
            ? step.plates.map((selected) => selected?.plate?.name || selected?.custom_name || selected?.name || selected?.plate_name || '')
            : [],
        }))
      : [],
  }));

  return {
    table_label: escapeText(payload.table_label || payload.tableLabel || ''),
    total: normalizeNumber(payload.total ?? payload.subtotal ?? payload.total_amount ?? 0),
    items,
  };
}

function buildFingerprintString(payload = {}) {
  return JSON.stringify(normalizeFingerprintPayload(payload));
}

function simpleHash(text) {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fp_${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function buildPosTicketText(payload = {}) {
  const items = extractItems(payload);
  const restaurantName = payload.restaurantName || payload.restaurant_name || 'Eat with Us TPV';
  const tableLabel = payload.tableLabel || payload.table_label || '-';
  const title = payload.title || 'PEDIDO POS';
  const orderLabel = payload.orderLabel
    || (payload.order_id ? `Pedido #${payload.order_id}` : 'Pedido local');
  const createdAt = payload.createdAt || payload.created_at || payload.created_at_local || Date.now();
  const total = payload.total ?? payload.subtotal ?? payload.total_amount ?? items.reduce((sum, item) => sum + normalizeNumber(item.unit_price ?? item.price ?? 0) * normalizeNumber(item.amount ?? item.quantity ?? 0), 0);

  const lines = [
    '--------------------------------',
    ` ${restaurantName}`,
    ` ${title}`,
    '--------------------------------',
    `Mesa: ${tableLabel}`,
    `Fecha: ${formatDateTime(createdAt)}`,
    '--------------------------------',
  ];

  items.forEach((item) => {
    lines.push(...buildItemLines(item));
    lines.push('');
  });

  lines.push('--------------------------------');
  lines.push(`Total: ${formatMoney(total)}`);
  lines.push('--------------------------------');
  lines.push('Ticket POS EWU');
  lines.push('');

  const fingerprintSource = buildFingerprintString(payload);

  return {
    content: lines.join('\n'),
    metadata: {
      restaurant_name: restaurantName,
      table_label: tableLabel,
      title,
      order_label: orderLabel,
      created_at: escapeText(createdAt),
      total: normalizeNumber(total),
      items_count: items.length,
      dedupe_key: payload.dedupe_key || simpleHash(fingerprintSource),
      source: payload.source || 'pos',
      order_id: payload.order_id || null,
      local_order_id: payload.local_order_id || null,
    },
  };
}

export async function buildPosTicketPrintPayload(payload = {}) {
  const ticket = buildPosTicketText(payload);
  const kitchenPayload = {
    restaurantName: payload.restaurantName || payload.restaurant_name || 'Eat with Us TPV',
    title: payload.title || 'COMANDA',
    tableLabel: payload.tableLabel || payload.table_label || '-',
    floorLabel: payload.floorLabel || payload.floor_label || '',
    orderLabel: payload.orderLabel || (payload.order_id ? `Pedido #${payload.order_id}` : 'Pedido local'),
    createdAt: payload.createdAt || payload.created_at || payload.created_at_local || Date.now(),
    items: extractItems(payload),
    footerText: 'Para cocina',
    metadata: payload.metadata || {},
    order_id: payload.order_id || null,
    local_order_id: payload.local_order_id || null,
  };
  let htmlContent = buildKitchenTicketHtml(kitchenPayload);
  let renderedTemplate = null;
  let templateSource = 'legacy';
  let templateMetadata = {};

  try {
    const rendered = await renderOperationalPrintEvent(PRINT_EVENT_KEYS.POS_ORDER_CREATED, kitchenPayload, {
      defaultTitle: 'COMANDA',
      footerMessage: 'Para cocina',
      originLabel: 'TPV',
      showPrices: false,
    });
    htmlContent = rendered.documentHtml;
    renderedTemplate = rendered.template;
    templateSource = rendered.source;
    templateMetadata = {
      print_template_key: rendered.template?.template_key || 'order_command',
      print_template_source: rendered.source || 'unknown',
      print_template_version: rendered.template?.version || null,
      print_template_payload: rendered.payload,
    };
  } catch (error) {
    console.warn('No se pudo renderizar la plantilla de cocina para la cola POS, se usa el HTML legado:', error);
  }

  const printJob = buildPrintJob({
    eventKey: PRINT_EVENT_KEYS.POS_ORDER_CREATED,
    htmlContent,
    template: renderedTemplate,
    templateSource,
    metadata: {
      ...ticket.metadata,
      ...payload.metadata,
      ...templateMetadata,
    },
  });
  return toPrintAgentPayload(printJob, {
    printer_name: payload.printer_name || null,
  });
}
